import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, AlertCircle, Inbox, ArrowRight, ListChecks } from 'lucide-react';
import { careEnquiryService } from '../../../services';
import { Button, PageHeader, Modal } from '../../../components';
import {
  wording, StatusBadge, statusLabel, nextAction, Journey, formatDate, formatDateTime,
  initials, errorMessage, inputCls, labelCls, panelCls,
  OPEN_STATUSES, PENDING_STATUSES, HISTORY_STATUSES,
} from './enquiryShared';

/**
 * Enquiries & Assessments — the list.
 *
 * Three tabs, matching the agreed flow:
 *   Open      — everything still being worked on (new → suitable)
 *   Pending   — handed over; these people are now in the Enquiry List waiting
 *               to be converted by the existing Convert form
 *   History   — converted or declined, kept with their assessment and decision
 */

const EnquiriesAssessments = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [mode, setMode] = useState('carehome');
  const [options, setOptions] = useState({ care_types: [], referral_sources: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState('open');
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const w = wording(mode);

  const blankForm = () => ({
    first_name: '', surname: '', contact_name: '', contact_relationship: '', contact_phone: '', address: '',
    referral_source: '', care_requested: '', reason: '',
    enquiry_date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    setLoading(true);
    try {
      const response = await careEnquiryService.list();
      if (response.success) {
        setEnquiries(response.data || []);
        setMode(response.mode || 'carehome');
        setOptions(response.options || { care_types: [], referral_sources: [] });
        setError('');
      } else {
        setError(response.message || 'Could not load enquiries');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not load enquiries'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const tabStatuses = tab === 'history' ? HISTORY_STATUSES : tab === 'pending' ? PENDING_STATUSES : OPEN_STATUSES;
  const inTab = useMemo(() => enquiries.filter((e) => tabStatuses.includes(e.status)), [enquiries, tab]);

  const filters = tab === 'open'
    ? [['all', 'All open enquiries'], ...['NEW', 'ASSESSMENT', 'REVIEW', 'DEFERRED', 'SUITABLE'].map((s) => [s, statusLabel(s, mode)])]
    : tab === 'history'
      ? [['all', 'All outcomes'], ['CONVERTED', `Converted to ${w.noun}`], ['DECLINED', 'Declined']]
      : [['all', w.pending]];

  const rows = useMemo(() => {
    const text = query.trim().toLowerCase();
    return inTab
      .filter((e) => filter === 'all' || e.status === filter)
      .filter((e) => !text || `${e.first_name} ${e.surname} ${e.reference} ${e.contact_name || ''} ${e.contact_phone || ''}`.toLowerCase().includes(text));
  }, [inTab, filter, query]);

  const counts = (status) => (status === 'all' ? inTab.length : inTab.filter((e) => e.status === status).length);

  const switchTab = (next) => { setTab(next); setFilter('all'); setQuery(''); };

  const submitEnquiry = async () => {
    if (!form.first_name.trim() || !form.surname.trim()) {
      setError('First name and surname are required');
      return;
    }
    setSaving(true);
    try {
      const response = await careEnquiryService.create(form);
      if (response.success) {
        setModalOpen(false);
        navigate(`/manager/enquiries-assessments/${response.data.id}`);
      } else {
        setError(response.message || 'Could not create the enquiry');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not create the enquiry'));
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, { type = 'text', options: opts, full = false, placeholder = '', required = false } = {}) => (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className={labelCls} htmlFor={`enq-${name}`}>
        {label}{required && <span className="text-rose-500"> *</span>}
      </label>
      {type === 'select' ? (
        <select id={`enq-${name}`} className={inputCls} value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })}>
          <option value="">Select an option</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea id={`enq-${name}`} rows={3} className={inputCls} placeholder={placeholder}
          value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
      ) : (
        <input id={`enq-${name}`} type={type} className={inputCls} placeholder={placeholder}
          value={form[name]} onChange={(e) => setForm({ ...form, [name]: e.target.value })} />
      )}
    </div>
  );

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        title="Enquiries & Assessments"
        subtitle="Manage enquiries, assessments and outcomes."
        actions={(
          <>
            <Button
              variant="secondary"
              icon={<RefreshCw className={`w-[18px] h-[18px] ${loading ? 'animate-spin' : ''}`} />}
              onClick={load}
              title="Refresh"
              aria-label="Refresh"
            />
            {/* The Enquiry List is only reachable from here: the Residents
                list's own Add button opens the profile form directly. */}
            <Button variant="secondary" leftIcon={<ListChecks className="w-4 h-4" />} onClick={() => navigate('/manager/enquiries')}>
              Enquiry list
            </Button>
            <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setForm(blankForm()); setError(''); setModalOpen(true); }}>
              New enquiry
            </Button>
          </>
        )}
      />

      {error && !modalOpen && (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl p-4 flex gap-3 text-red-700 dark:text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <Journey status="NEW" mode={mode} />

      {/* Tabs */}
      <div className="flex gap-6 border-b border-[#E3E9F3] dark:border-gray-800" role="group" aria-label="Enquiry lists">
        {[
          ['open', 'Open enquiries', enquiries.filter((e) => OPEN_STATUSES.includes(e.status)).length],
          ['pending', w.pending, enquiries.filter((e) => PENDING_STATUSES.includes(e.status)).length],
          ['history', 'History', enquiries.filter((e) => HISTORY_STATUSES.includes(e.status)).length],
        ].map(([id, label, count]) => (
          <button
            key={id}
            type="button"
            onClick={() => switchTab(id)}
            aria-pressed={tab === id}
            className={`flex items-center gap-2 pb-3 -mb-px border-b-2 text-[13.5px] font-semibold transition ${tab === id
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-[#7B8DA6] hover:text-[#475569]'}`}
          >
            {label}
            <span className={`px-1.5 py-0.5 rounded text-[11px] ${tab === id ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20' : 'bg-[#E7EDF7] text-[#64748B] dark:bg-gray-800 dark:text-gray-400'}`}>{count}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[200px_minmax(0,1fr)] gap-5 items-start">
        {/* Status filters */}
        <aside className={`${panelCls} p-3`} aria-label="Filter by status">
          <h2 className="text-[10.5px] font-bold tracking-wider text-[#8292AA] px-2.5 mt-1 mb-2">
            {tab === 'history' ? 'OUTCOME' : 'ENQUIRY STATUS'}
          </h2>
          {filters.map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              aria-pressed={filter === value}
              className={`w-full flex items-center justify-between gap-2 text-left px-2.5 py-2.5 rounded-lg text-[12.5px] my-0.5 transition ${filter === value
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 font-semibold'
                : 'text-[#536580] dark:text-gray-400 hover:bg-[#F5F8FD] dark:hover:bg-[#31373f]'}`}
            >
              <span>{label}</span>
              <small className="px-1.5 py-0.5 rounded bg-[#F0F3F8] dark:bg-gray-800 text-[11px]">{counts(value)}</small>
            </button>
          ))}
        </aside>

        {/* List */}
        <section className={panelCls}>
          <div className="px-5 pt-5 pb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100">
              {tab === 'history' ? 'Enquiry history' : tab === 'pending' ? w.pending : 'Open enquiries'}
            </h2>
            <span className="text-[12px] text-[#94A3B8]">{rows.length} {rows.length === 1 ? 'person' : 'people'}</span>
          </div>

          <div className="px-5 pb-4 border-b border-[#E3E9F3] dark:border-gray-800">
            <input
              className={inputCls}
              placeholder="Search by name, enquiry reference or contact…"
              aria-label="Search enquiries"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <p className="p-10 text-center text-[13px] text-[#94A3B8]">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <Inbox className="w-8 h-8 mx-auto text-[#7C9EDB] mb-3" />
              <h3 className="text-[15px] font-bold text-[#1E293B] dark:text-gray-100">
                {query || filter !== 'all' ? 'No matching enquiries' : tab === 'history' ? 'No completed enquiries yet' : tab === 'pending' ? `No ${w.pending.toLowerCase()}` : 'Your enquiries will appear here'}
              </h3>
              <p className="text-[12.5px] text-[#91A0B5] max-w-[380px] mx-auto mt-2">
                {query || filter !== 'all'
                  ? 'Try another name, or change the filter.'
                  : tab === 'history'
                    ? 'Converted and declined enquiries move here, with their assessment and decision attached.'
                    : tab === 'pending'
                      ? `People appear here once their ${w.admission} has been planned, ready to convert.`
                      : 'Add an enquiry to begin. Each person keeps their own status, assessment and next action.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[660px] border-collapse">
                <thead>
                  <tr className="bg-[#F8FAFF] dark:bg-[#1f242b]">
                    {['PERSON', tab === 'history' ? 'OUTCOME' : 'CONTACT', tab === 'history' ? 'COMPLETED' : 'STATUS', tab === 'history' ? 'REASON' : 'UPDATED', ''].map((h, i) => (
                      <th key={i} className="text-left text-[10px] tracking-wider font-bold text-[#8191A9] px-5 py-3.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id} className="border-t border-[#EDF1F7] dark:border-gray-800">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-xl bg-[#E6EFFF] dark:bg-blue-900/20 text-[#2F62D3] dark:text-blue-300 grid place-items-center text-[12px] font-bold shrink-0">
                            {initials(e)}
                          </span>
                          <div className="min-w-0">
                            <strong className="block text-[13px] font-semibold text-[#24374F] dark:text-gray-100">{e.first_name} {e.surname}</strong>
                            <small className="block text-[11px] text-[#97A4B8]">{e.reference}</small>
                          </div>
                        </div>
                      </td>
                      {tab === 'history' ? (
                        <>
                          <td className="px-5 py-4"><StatusBadge status={e.status} mode={mode} /></td>
                          <td className="px-5 py-4 text-[12px] text-[#6B7C95]">{formatDateTime(e.converted_at || e.updated_at)}</td>
                          <td className="px-5 py-4 text-[12px] text-[#6B7C95] max-w-[260px]">{e.review?.rationale || 'Not recorded'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-5 py-4 text-[12px] text-[#6B7C95]">
                            {e.contact_name || 'Not recorded'}
                            {e.contact_phone && <small className="block text-[11px] text-[#97A4B8] mt-1">{e.contact_phone}</small>}
                          </td>
                          <td className="px-5 py-4"><StatusBadge status={e.status} mode={mode} /></td>
                          <td className="px-5 py-4 text-[12px] text-[#6B7C95] whitespace-nowrap">{formatDate(e.updated_at || e.created_at)}</td>
                        </>
                      )}
                      <td className="px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => navigate(`/manager/enquiries-assessments/${e.id}`)}
                          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap"
                        >
                          {tab === 'history' ? 'View history' : nextAction(e.status, mode)}
                          <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <p className="text-[11.5px] text-[#94A1B4]">
        Approved and planned enquiries also appear in the Enquiry List, ready to convert to a {w.noun}.
      </p>

      {/* New enquiry */}
      {form && (
        <Modal
          isOpen={modalOpen}
          onClose={() => !saving && setModalOpen(false)}
          title="New enquiry"
          maxWidth="max-w-2xl"
          footer={(
            <div className="flex justify-end gap-3 w-full">
              <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button variant="primary" onClick={submitEnquiry} loading={saving}>Create enquiry</Button>
            </div>
          )}
        >
          {error && (
            <div className="mb-4 text-[12.5px] text-rose-600 dark:text-rose-400">{error}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('first_name', 'First name', { required: true, placeholder: 'e.g. Margaret' })}
            {field('surname', 'Surname', { required: true, placeholder: 'e.g. Ellis' })}
            {field('contact_name', 'Enquirer / contact name', { placeholder: 'e.g. Emma Ellis' })}
            {/* The assessor rings this person to arrange the visit, and the
                relationship is what makes that call make sense. */}
            {field('contact_relationship', 'Relationship to the person', {
              type: 'select',
              options: ['Daughter', 'Son', 'Wife', 'Husband', 'Partner', 'Sibling', 'Parent', 'Friend', 'Advocate / representative', 'Social worker', 'Self', 'Other'],
            })}
            {field('contact_phone', 'Contact number', { type: 'tel', placeholder: 'e.g. 07700 900123' })}
            {field('referral_source', 'Referral source', { type: 'select', options: options.referral_sources })}
            {field('care_requested', 'Care requested', { type: 'select', options: options.care_types })}
            {field('address', 'Address', { full: true, placeholder: 'e.g. 12 High Street, London, SW1A 1AA' })}
            {field('enquiry_date', 'Enquiry date', { type: 'date' })}
            {field('reason', 'Reason for enquiry', { type: 'textarea', full: true, placeholder: 'A short summary of the support being requested.' })}
          </div>
          <p className="text-[11.5px] text-[#94A3B8] mt-4">
            This creates an enquiry only. It does not add an active {w.noun}.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default EnquiriesAssessments;
